import React from 'react';
import { Link } from 'react-router-dom';
import { Divider, Row, Col } from 'antd';
import { isNull } from 'lodash';

import {
  ContactPersonModel,
  CustomerInfoResponseModel,
} from 'api/backofficeApiClient';
import BasePageCard from 'components/wrappers/BasePageCard';
import PrimaryContactInfoSection from 'components/PrimaryContactInfoSection';
import { getWebsiteName, getWebsiteLink } from 'utils/urlUtils';
import {
  getClientSizeLabel,
  getAbilityToPayLabel,
  getYearsInBusinessLabel,
  getProjectEstimation,
} from 'utils/clientUtils';
import { getLocation } from 'utils/locationUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { getFullName } from 'utils/personUtils';
import { formatNumberAsCurrency } from 'utils/numberUtils';

type Props = {
  clientDetails: CustomerInfoResponseModel;
};

export default function ClientMainInfoSection(props: Props) {
  const isAdminRole = useAppSelector(isAdminSelector);
  const { clientDetails } = props;
  const {
    customerId,
    customerKey,
    customerInfo,
    primaryContacts,
    billingInfo,
    additionalInfo,
    recruiterCompanyName,
    recruiterCompanyKey,
    customerGroupName,
  } = clientDetails;
  const {
    countryName,
    address,
    city,
    internationalCityName,
    state,
    internationalStateName,
    zip,
  } = customerInfo.location;
  const mainAddress = getLocation(
    countryName,
    address,
    state || internationalStateName,
    city || internationalCityName,
    zip,
  );
  const billingContacts = billingInfo.billingContacts || [];
  const isContactFilled = (contact: ContactPersonModel) =>
    !!contact.email ||
    !!contact.firstName ||
    !!contact.lastName ||
    !!contact.title ||
    !!contact.phoneNumber;

  const isPrimaryContactVisible = primaryContacts.some((contact) =>
    isContactFilled(contact),
  );
  const isGeneralInfoVisible = !!(
    isPrimaryContactVisible ||
    customerId ||
    recruiterCompanyName ||
    mainAddress ||
    customerGroupName ||
    customerInfo.companyWebSite
  );

  const isBillingInfoVisible =
    billingContacts.length ||
    (billingInfo.location &&
      Object.values(billingInfo?.location).some(Boolean));

  const billingAddress = getLocation(
    billingInfo?.location?.countryName,
    billingInfo?.location?.address,
    billingInfo?.location?.state ||
      billingInfo?.location?.internationalStateName,
    billingInfo?.location?.city || billingInfo?.location?.internationalCityName,
    billingInfo?.location?.zip,
  );

  return (
    <Row gutter={[10, 10]}>
      {isGeneralInfoVisible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="General information"
            icons={<span className="icon icon-writing-tool-user"></span>}
          >
            {!!(
              customerKey ||
              mainAddress ||
              recruiterCompanyName ||
              customerGroupName ||
              customerInfo.companyWebSite
            ) && (
              <div className="content-wrapper">
                <Row gutter={[4, 4]} className="titled-sheet">
                  {customerKey && (
                    <Col xs={24} md={12} data-id="customerId">
                      <div className="title">Customer ID:</div>
                      <div>{customerKey}</div>
                    </Col>
                  )}

                  {recruiterCompanyName && (
                    <Col xs={24} md={12} data-id="customerMemberCompany">
                      <div className="title">Member company:</div>

                      {isAdminRole ? (
                        <Link to={`/member/${recruiterCompanyKey}`}>
                          {recruiterCompanyName}
                        </Link>
                      ) : (
                        <div>{recruiterCompanyName}</div>
                      )}
                    </Col>
                  )}

                  {customerInfo.additionalInvoiceName && (
                    <Col xs={24} md={12}>
                      <div className="title">Invoice name (Legal/DBA):</div>
                      <div>{customerInfo.additionalInvoiceName}</div>
                    </Col>
                  )}

                  {mainAddress && (
                    <Col xs={24} md={12} data-id="customerAddress">
                      <div className="title">Head-office address:</div>
                      <div>{mainAddress}</div>
                    </Col>
                  )}

                  {customerInfo.companyWebSite && (
                    <Col xs={24} md={12} data-id="customerWebsite">
                      <div className="title">Company website:</div>
                      <div>
                        <a
                          href={getWebsiteLink(customerInfo.companyWebSite)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {getWebsiteName(customerInfo.companyWebSite)}
                        </a>
                      </div>
                    </Col>
                  )}

                  {customerInfo.companyEmail && (
                    <Col xs={24} md={12} data-id="customerAddress">
                      <div className="title">Email:</div>
                      <div>{customerInfo.companyEmail}</div>
                    </Col>
                  )}

                  {customerInfo.companyPhoneNumber && (
                    <Col xs={24} md={12} data-id="customerCompanyPhoneNumber">
                      <div className="title">Phone number:</div>
                      <div>{customerInfo.companyPhoneNumber}</div>
                    </Col>
                  )}

                  {customerGroupName && (
                    <Col xs={24} md={12} data-id="customerGroup">
                      <div className="title">Customer Group:</div>
                      <div>{customerGroupName}</div>
                    </Col>
                  )}
                </Row>
              </div>
            )}

            {isPrimaryContactVisible && (
              <>
                <Divider className="no-margin" />

                <Row gutter={[4, 4]} className="titled-sheet">
                  <Col xs={24}>
                    <div className="subtitle subtitle-indent">
                      Primary contact
                    </div>
                  </Col>

                  <Col xs={24}>
                    <div className="scroll-box scroll-box-lg">
                      {primaryContacts.map(
                        (contact, index) =>
                          isContactFilled(contact) && (
                            <PrimaryContactInfoSection
                              index={index}
                              contact={contact}
                              key={`client-primary-contact-${index}`}
                            />
                          ),
                      )}
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </BasePageCard>
        </Col>
      )}

      <Col xs={24} md={12} className="d-flex">
        <Row gutter={[10, 10]}>
          {isBillingInfoVisible && (
            <Col xs={24} className="d-flex">
              <BasePageCard
                className="stretch-card"
                title="Billing information"
                icons={<span className="icon icon-pay"></span>}
              >
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    {billingContacts.map((billingInfo, index) => (
                      <React.Fragment key={index}>
                        {(billingInfo?.firstName || billingInfo.lastName) && (
                          <Col xs={24} md={12} data-id="customerAp">
                            <div className="title">A/P: </div>
                            <div>
                              {getFullName(
                                billingInfo.firstName,
                                billingInfo.lastName,
                              )}
                            </div>
                          </Col>
                        )}

                        {billingInfo?.email && (
                          <Col xs={24} md={12} data-id="customerApEmail">
                            <div className="title">A/P email: </div>
                            <div>{billingInfo.email}</div>
                          </Col>
                        )}

                        {billingInfo?.phoneNumber && (
                          <Col xs={24} md={12} data-id="customerApPhoneNumber">
                            <div className="title">A/P phone number: </div>
                            <div>{billingInfo?.phoneNumber}</div>
                          </Col>
                        )}
                      </React.Fragment>
                    ))}
                    {billingAddress && (
                      <Col xs={24} md={12} data-id="customerBillingAddress">
                        <div className="title">Billing address: </div>
                        <div>{billingAddress}</div>
                      </Col>
                    )}
                  </Row>
                </div>
              </BasePageCard>
            </Col>
          )}

          <Col xs={24} className="d-flex">
            <BasePageCard
              className="stretch-card"
              title="Additional information"
              icons={<span className="icon icon-writing-tool"></span>}
            >
              <div className="content-wrapper">
                <Row gutter={[4, 4]} className="titled-sheet">
                  <Col xs={24} md={12} data-id="customerWorkWithCustomerBefore">
                    <div className="title">Worked with customer before: </div>
                    <div>
                      {additionalInfo?.haveBusinessBefore ? 'Yes' : 'No'}
                    </div>
                  </Col>

                  {!isNull(additionalInfo?.industryName) && (
                    <Col xs={24} md={12} data-id="customerIndustry">
                      <div className="title">Industry: </div>
                      <div>{additionalInfo.industryName}</div>
                    </Col>
                  )}

                  {!isNull(additionalInfo?.yearsInBusiness) && (
                    <Col xs={24} md={12} data-id="customerYearsInBusiness">
                      <div className="title">Years in business: </div>
                      <div>
                        {getYearsInBusinessLabel(
                          additionalInfo?.yearsInBusiness,
                        )}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfo?.abilityToPay) && (
                    <Col xs={24} md={12} data-id="customerAbilityToPay">
                      <div className="title">Ability to pay: </div>
                      <div>
                        {getAbilityToPayLabel(additionalInfo?.abilityToPay)}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfo?.averageBillRate) && (
                    <Col xs={24} md={12} data-id="customerAverageBillRate">
                      <div className="title">Average bill rate: </div>
                      <div>
                        {formatNumberAsCurrency({
                          num: additionalInfo.averageBillRate,
                        })}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfo?.potentialHeadcount) && (
                    <Col xs={24} md={12} data-id="customerPotentialHeadcount">
                      <div className="title">Potential headcount: </div>
                      <div>
                        {getClientSizeLabel(additionalInfo?.potentialHeadcount)}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfo?.estimatedValue) && (
                    <Col xs={24} md={12} data-id="customerProjectLength">
                      <div className="title">
                        Estimated length of the project:{' '}
                      </div>
                      <div>
                        {getProjectEstimation(
                          additionalInfo?.estimatedValue,
                          additionalInfo?.estimatedType,
                        )}
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </BasePageCard>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
